// React imports
import React, { useState, type JSX } from "react";

// Import typed Redux hooks
import { useAppSelector } from "./hooks/reduxHooks";

import SidebarBrand from "./components/sidebar/SidebarBrand";
import SidebarChats from "./components/sidebar/SidebarChats";
import SidebarFooter from "./components/sidebar/SidebarFooter";
import SidebarModals from "./components/sidebar/SidebarModals";
import { useActiveLinkSync } from "./components/sidebar/useActiveLinkSync";
import type { ChatTarget, SidebarProject } from "./components/sidebar/ChatList";

interface SidebarComponentProps {
  isCollapsed?: boolean;
  onCollapseToggle?: (collapsed: boolean) => void;
  onStartProject?: () => void;
  onClose?: () => void;
  projects?: SidebarProject[];
  currentProjectId?: string | null;
  onProjectClick?: (projectId: string) => void;
  projectsLoading?: boolean;
}

/**
 * Clicking anywhere on the collapsed rail expands it, except on a button —
 * user action should override auto-collapse in workspace mode.
 */
function expandOnRailClick(
  event: React.MouseEvent<HTMLElement>,
  isCollapsed: boolean,
  onCollapseToggle?: (collapsed: boolean) => void
): void {
  if ((event.target as HTMLElement).closest("button")) {
    return;
  }
  if (isCollapsed && onCollapseToggle) {
    onCollapseToggle(false);
  }
}

/** Navigate to the admin/auditing interface in a new tab. */
function openConversationInAdmin(conversationId: string, onClose?: () => void): void {
  window.open(`/admin/conversation/${conversationId}`, "_blank", "noopener,noreferrer");
  // Close mobile sidebar when navigating
  onClose && onClose();
}

// Default export of Sidebar_Component
export default function Sidebar_Component({
  isCollapsed = false,
  onCollapseToggle,
  onStartProject,
  onClose,
  projects = [],
  currentProjectId = null,
  onProjectClick,
  projectsLoading = false,
}: SidebarComponentProps): JSX.Element {
  const conversationId = useAppSelector((state) => state.chatSlice.conversationId);
  const isAdmin = useAppSelector((state) => state.userAuthAndAdminSlice.isAdmin);

  useActiveLinkSync();

  const [renameTarget, setRenameTarget] = useState<ChatTarget | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ChatTarget | null>(null);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState<boolean>(false);

  return (
    <aside
      className={`bg-white h-full border-r border-gray-200 flex flex-col transition-all duration-200 ${
        isCollapsed ? "w-20 cursor-e-resize" : "w-64"
      }`}
      onClick={(e) => expandOnRailClick(e, isCollapsed, onCollapseToggle)}
    >
      <SidebarBrand
        isCollapsed={isCollapsed}
        onCollapseToggle={onCollapseToggle}
        onStartProject={onStartProject}
      />

      <SidebarChats
        isCollapsed={isCollapsed}
        projects={projects}
        projectsLoading={projectsLoading}
        currentProjectId={currentProjectId}
        onStartProject={onStartProject}
        onProjectClick={onProjectClick}
        onOpenSearch={() => setIsSearchModalOpen(true)}
        onRename={setRenameTarget}
        onDelete={setDeleteTarget}
      />

      {/* Admin/Auditing button - fixed at bottom, only visible to admins */}
      {isAdmin && conversationId && (
        <SidebarFooter
          isCollapsed={isCollapsed}
          onViewInAdmin={() => openConversationInAdmin(conversationId, onClose)}
        />
      )}

      <SidebarModals
        renameTarget={renameTarget}
        deleteTarget={deleteTarget}
        isSearchModalOpen={isSearchModalOpen}
        currentProjectId={currentProjectId}
        onCloseRename={() => setRenameTarget(null)}
        onCloseDelete={() => setDeleteTarget(null)}
        onCloseSearch={() => setIsSearchModalOpen(false)}
        onStartProject={onStartProject}
        onProjectClick={onProjectClick}
      />
    </aside>
  );
}
